import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import {
  IconLoader,
  ButtonPseudo,
  IconRefresh,
  ButtonSize,
  IconDownload,
  PermissionEnum,
  PermissionDenied,
  ToggleInput,
  DateInput,
  DateInputType,
  DateInputRange,
  Checkbox,
  ButtonSecondary,
  ButtonPrimary,
  SidePanelType,
  SidePanel,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import CapitalAdequacy from '../../components/capital-adequacy/capital-adequacy';
import { changeVisibility } from './store/slice';
import {
  useLazyGetCapitalAdequacyReportQuery,
  useExportCapitalAdequacyReportMutation,
  useExportCapitalAdequacyReportWithAccoubtsMutation,
} from './store/service';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { AccountType } from '../types';
import { CapitalAdequacy as CapitalAdequacyProps } from './types';
import { setNotificationVisible } from '../../../../../store/slice';
import { clearAccountingJobs } from '../../store/dictionaries/slice';
import PrecisionSelector from '../../components/partial/reports/precision-selector';

import cn from './capital-adequacy.module.css';

let timeout: NodeJS.Timeout;

const CapitalAdequacyHOC = () => {
  const [date, setDate] = useState<Date>();
  const [downloadReportVisible, setDownloadReportVisible] = useState(false);
  const [downloadReportDates, setDownloadReportDates] = useState<Date[]>([]);
  const [type, setType] = useState('without');
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();

  const [errorList, setErrorList] = useState('');
  const [excludeRevaluation, setExcludeRevaluation] = useState<boolean>(false);
  const [precision, setPrecision] = useState('2');

  const {
    capitalAdequacy: {
      report,
      itemsVisible,
      formationTime,
      ongoingCapitalRequirement,
      excessEligibleEquity,
      minimalCapitalRequirement,
      applicableOngoingCapitalRequirement,
      eligibleEquity,
      averageOutstandingEmoney,
      accountsCategories,
    },
    reportsDictionaries: { activePermissions, accountingJobs },
  } = useAppSelector((state) => state);

  const { search } = useLocation();

  const navigate = useNavigate();

  const [getCapitalAdequacyReport, { isLoading, isFetching }] =
    useLazyGetCapitalAdequacyReportQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();

  const [
    exportCapitalAdequacy,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportCapitalAdequacyReportMutation();

  const [
    exportCapitalAdequacyWithAccounts,
    {
      isLoading: exportWithAccountsInProgress,
      isSuccess: exportWithAccountsIsSuccess,
    },
  ] = useExportCapitalAdequacyReportWithAccoubtsMutation();

  const dispatch = useAppDispatch();

  const getCapitalAdequacyReportAction = useCallback(
    async (searchValue: string) => {
      const response = (await getCapitalAdequacyReport(
        searchValue,
      )) as unknown as {
        data?: CapitalAdequacyProps;
        error?: ResponseError;
        statusCode: number;
      };

      if (response?.error) {
        getResponseErrors({
          error: response.error,
          setErrorList,
        });
      }
    },
    [],
  );

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onVisibleChange = (accountType: AccountType) => {
    let nextAccountsType = ([] as AccountType[]).concat(itemsVisible);

    if (nextAccountsType.includes(accountType)) {
      nextAccountsType = nextAccountsType.filter(
        (item) => item !== accountType,
      );

      if (
        accountType === AccountType.GROUP &&
        nextAccountsType.includes(AccountType.PRIMARY_ACCOUNT)
      ) {
        nextAccountsType = [];
      }
    } else {
      nextAccountsType.push(accountType);

      if (
        accountType === AccountType.PRIMARY_ACCOUNT &&
        !nextAccountsType.includes(AccountType.GROUP)
      ) {
        nextAccountsType.push(AccountType.GROUP);
      }
    }

    dispatch(changeVisibility(nextAccountsType));
  };

  const onExportClick = async () => {
    const body: {
      createdAtFrom?: string;
      createdAtTo?: string;
      roundDecimals?: string;
    } = {};

    if (downloadReportDates && downloadReportDates.length) {
      body.createdAtFrom = new Date(downloadReportDates[0]).toISOString();

      if (downloadReportDates.length > 1) {
        body.createdAtTo = new Date(downloadReportDates[1]).toISOString();
      }
    }

    if (precision !== '2') {
      body.roundDecimals = precision;
    }

    if (type === 'without') {
      await exportCapitalAdequacy(body);
    } else if (type === 'with') {
      await exportCapitalAdequacyWithAccounts(body);
    }
  };

  useEffect(() => {
    if (exportIsSuccess || exportWithAccountsIsSuccess) {
      setDownloadReportVisible(false);
      setDownloadReportDates([date || new Date()]);
      setType('without');

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'capital-adequacy-report',
        }),
      );
    }
  }, [exportIsSuccess, exportWithAccountsIsSuccess]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
      ) &&
      !search &&
      accountsCategories
    ) {
      const firstDateOfMonth = new Date();

      firstDateOfMonth.setDate(1);
      firstDateOfMonth.setHours(0);
      firstDateOfMonth.setMinutes(0);
      firstDateOfMonth.setSeconds(0);
      firstDateOfMonth.setMilliseconds(0);

      navigate(
        `?transferDateFrom=${format(new Date(firstDateOfMonth), 'yyyy-MM-dd')}`,
      );

      setDate(new Date(firstDateOfMonth));
      setDownloadReportDates([new Date(firstDateOfMonth)]);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
      ) &&
      search &&
      accountsCategories
    ) {
      const parsed = queryString.parse(search);

      if (parsed?.transferDateFrom) {
        setDate(new Date(parsed?.transferDateFrom as string));
        setDownloadReportDates([new Date(parsed?.transferDateFrom as string)]);
      }

      if (parsed?.excludeRevaluation) {
        setExcludeRevaluation(Boolean(parsed?.excludeRevaluation === 'true'));
      }

      if (parsed.roundDecimals) {
        setPrecision(parsed.roundDecimals as string);
      }

      getCapitalAdequacyReportAction(search);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    let title = 'Capital adequacy report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let globalContent = (
    <div className={cn.globalLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(
        PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
      ) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      globalContent = <PermissionDenied />;
    } else if (
      activePermissions.includes(PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST)
    ) {
      globalContent = (
        <div className={cn.capitalAdequacy}>
          <div className={cn.capitalAdequacyTop}>
            <div className={cn.capitalAdequacyTitle}>
              Capital adequacy report (e-money and related payment services)
            </div>
            <div className={cn.capitalAdequacyTopControls}>
              <div className={cn.capitalAdequacyTopControlsLeft}>
                <DateInput
                  type={DateInputType.PSEUDO}
                  placeholder="Select Date"
                  value={date?.toISOString()}
                  className={cn.capitalAdequacyDateInput}
                  onReset={() => {
                    let link = '';

                    if (excludeRevaluation) {
                      link = `?excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                    setDate(undefined);
                    setDownloadReportDates([]);
                  }}
                  onChange={(e) => {
                    setDate(new Date(e));
                    setDownloadReportDates([new Date(e)]);

                    let link = `?transferDateFrom=${format(
                      new Date(e),
                      'yyyy-MM-dd',
                    )}`;

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                  }}
                />
              </div>
              <div className={cn.capitalAdequacyTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={report && isFetching ? cn.refreshing : ''}
                    />
                  }
                  className={`button-white ${cn.profitLossRefreshButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    getCapitalAdequacyReportAction(search);
                    setErrorList('');
                  }}
                  disabled={isLoading || isFetching}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_CAPITAL_ADEQUACY_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text="Download Report"
                    icon={<IconDownload />}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => setDownloadReportVisible(true)}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.capitalAdequacyVisibilityControls}>
              <div className={cn.capitalAdequacyVisibilityControlsLeft}>
                <ToggleInput
                  name="groups"
                  title="Groups"
                  checked={itemsVisible.includes(AccountType.GROUP)}
                  onChange={() => onVisibleChange(AccountType.GROUP)}
                  className={cn.capitalAdequacyVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
                <ToggleInput
                  name="primary"
                  title="Primary Accounts"
                  checked={itemsVisible.includes(AccountType.PRIMARY_ACCOUNT)}
                  onChange={() => onVisibleChange(AccountType.PRIMARY_ACCOUNT)}
                  className={cn.capitalAdequacyVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
                <ToggleInput
                  name="excludeRevaluation"
                  title="Exclude Revaluation"
                  checked={excludeRevaluation}
                  onChange={(e) => {
                    setExcludeRevaluation(e.target.checked);

                    let link = '';

                    if (e.target.checked) {
                      link = `${link}excludeRevaluation=${e.target.checked}`;
                    }

                    if (date) {
                      link = `${link}${
                        e.target.checked ? '&' : ''
                      }transferDateFrom=${format(
                        new Date(date),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(`?${link.replace('?', '')}`);
                  }}
                  className={cn.capitalAdequacyVisibilityToggle}
                  disabled={Boolean(errorList)}
                />
              </div>
              <div className={cn.safeguardingSheetVisibilityControlsRight}>
                <PrecisionSelector
                  precision={precision}
                  setPrecision={(value) => {
                    setPrecision(value);

                    let link = `?roundDecimals=${value}`;

                    if (date) {
                      link = `${link}&transferDateFrom=${format(
                        new Date(date),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    navigate(`?${link.replace('?', '')}`);
                  }}
                />
              </div>
            </div>
            <div className={cn.capitalAdequacySearchInfo}>
              {formationTime?.end
                ? `Formed in ${formationTime?.duration} sec: ${new Date(
                    formationTime?.start || '',
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })} from ${new Date(formationTime?.start || '')
                    .toLocaleString()
                    .slice(-8)} to ${new Date(formationTime?.end)
                    .toLocaleString()
                    .slice(-8)}`
                : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJob={currentAccountingJob}
                accountingJobs={accountingJobs}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW"
                withoutMargin
              />
            )}
          </div>
          {errorList && (
            <div className={cn.reportsLoadingError}>
              <div className={cn.reportsLoadingErrorTitle}>
                Unable to load data
              </div>
              <div className={cn.reportsLoadingErrorText}>{errorList}</div>
            </div>
          )}
          {!errorList && (
            <CapitalAdequacy
              itemsVisible={itemsVisible}
              items={report}
              ongoingCapitalRequirement={ongoingCapitalRequirement}
              excessEligibleEquity={excessEligibleEquity}
              minimalCapitalRequirement={minimalCapitalRequirement}
              applicableOngoingCapitalRequirement={
                applicableOngoingCapitalRequirement
              }
              search={search}
              eligibleEquity={eligibleEquity}
              averageOutstandingEmoney={averageOutstandingEmoney}
              isLoading={isLoading || isFetching || !accountsCategories}
            />
          )}

          <SidePanel
            title="Download report"
            isOpen={downloadReportVisible}
            onClose={() => {
              setDownloadReportVisible(false);
              setDownloadReportDates([date || new Date()]);
              setType('without');
            }}
            type={SidePanelType.AML}
            sidepanelExpandedKey="download-report-panel"
            footer={
              <div className={cn.statementPanelControls}>
                <ButtonPrimary
                  text={
                    exportInProgress || exportWithAccountsInProgress
                      ? 'Loading...'
                      : 'Download'
                  }
                  icon={<IconDownload />}
                  disabled={exportInProgress || exportWithAccountsInProgress}
                  onClick={onExportClick}
                  bold
                />
                <ButtonSecondary
                  className="button-white"
                  text="Close"
                  onClick={() => {
                    setDownloadReportDates([date || new Date()]);
                    setDownloadReportVisible(false);
                    setType('without');
                  }}
                  bold
                />
              </div>
            }
            closeOnClickOutside
          >
            <>
              <div className={cn.exportTypeTitle}>Export type</div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="without"
                  title="Without accounts"
                  checked={type === 'without'}
                  onChange={() => setType('without')}
                />
              </div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="with"
                  title="With accounts"
                  checked={type === 'with'}
                  onChange={() => setType('with')}
                />
              </div>
              <div
                className={`${cn.datesWrapper} ${
                  !downloadReportDates.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>Report period</div>
                <DateInputRange
                  values={downloadReportDates?.map((item) =>
                    item.toISOString(),
                  )}
                  onChangeRange={(dateValues) =>
                    setDownloadReportDates(dateValues)
                  }
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            </>
          </SidePanel>
        </div>
      );
    }
  }

  return globalContent;
};

export default CapitalAdequacyHOC;
